import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const LEAVE_TYPES = [
  { v: "general", label: "General" },
  { v: "sick", label: "Sick" },
  { v: "probation", label: "Probation" },
  { v: "short_break", label: "Short break" },
  { v: "bereavement", label: "Bereavement" },
];

const EMPTY = { leave_type: "general", start_date: "", end_date: "", reason: "", half_day: "no" };

/** Calendar days a leave consumes — mirrors Leaves.jsx's computeDays(). */
function computeDays(form) {
  const { start_date, end_date, half_day } = form || {};
  if (!start_date || !end_date) return "—";
  const sd = new Date(start_date);
  const ed = new Date(end_date);
  if (Number.isNaN(sd.getTime()) || Number.isNaN(ed.getTime()) || ed < sd) return "—";
  const span = Math.round((ed - sd) / 86400000) + 1;
  if ((half_day === "first_half" || half_day === "second_half") && span === 1) return "0.5";
  return String(span);
}

/**
 * Feb 2026 — Home Command Center's "New Leave" button. Standalone (no
 * Leaves.jsx page context — balances/calendar/team view are irrelevant
 * here) so it opens as a direct modal on the Dashboard. Backend
 * (`require_internal`) already lets any internal staff member apply, so
 * this must never depend on a Leaves page-level permission.
 */
export default function ApplyLeaveDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(EMPTY);

  const apply = async () => {
    if (!form.start_date || !form.end_date) { toast.error("Pick a start and end date"); return; }
    try {
      const payload = { ...form };
      if (payload.half_day === "no") delete payload.half_day;
      await api.post("/leaves", payload);
      toast.success("Leave applied");
      onOpenChange(false);
      setForm(EMPTY);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setForm(EMPTY); }}>
      <DialogContent className="rounded-sm" data-testid="apply-leave-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Apply for leave</DialogTitle>
          <DialogDescription>Submitted for your reporting manager/HR to review and approve.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="label-overline">Type</Label>
            <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="npl-leave-type"><SelectValue /></SelectTrigger>
              <SelectContent>{LEAVE_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-overline">Start</Label><Input type="date" className="rounded-sm mt-1" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="npl-start" /></div>
            <div><Label className="label-overline">End</Label><Input type="date" className="rounded-sm mt-1" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="npl-end" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-overline">Half Day?</Label>
              <Select value={form.half_day} onValueChange={(v) => setForm({ ...form, half_day: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="npl-half-day"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">No — Full Day</SelectItem>
                  <SelectItem value="first_half">Yes — First Half</SelectItem>
                  <SelectItem value="second_half">Yes — Second Half</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-overline">Number of days</Label>
              <Input readOnly className="rounded-sm mt-1 bg-slate-50 font-mono text-sm text-slate-700" value={computeDays(form)} data-testid="npl-days-calc" />
            </div>
          </div>
          <div><Label className="label-overline">Reason</Label><Textarea rows={3} className="rounded-sm mt-1" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="npl-reason" /></div>
        </div>
        <DialogFooter><Button onClick={apply} data-testid="npl-submit" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">Submit</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
